import { Injectable } from '@angular/core';
import { AuthState } from '../interfaces/IAuthState';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { AuthCallbackRequest, CheckCprNumberRequest } from '../interfaces/IAuthCallbackRequest ';
import { UserInfo } from '../interfaces/IUserInfo';
import * as CryptoJS from 'crypto-js';
import { AppConfigService } from './app-config.service';
import { catchError } from 'rxjs/operators';
import { IAuthResponse, OperationResponse } from '../interfaces/IAuthResponse';
import { BsModalService } from 'ngx-bootstrap';
import { PopupNotificationComponent } from '../request/popup-notification/popup-notification.component';
import { Mode } from '../form-guide/utility/enums/enums';

@Injectable({
  providedIn: 'root'
})
export class EKeyAuthService {
  private clientId: string = '';
  private redirectUri: string = '';
  private ekeyBaseUrl: string = '';
  private backendUrl: string = '';
  public eKeyAuthUrl: string = '';
  public authUrl: string = '';
  public IsProduction: boolean;
  public IsEKey2: boolean;
  public IsEKey: boolean;

  private authState = new BehaviorSubject<AuthState>({
    isAuthenticated: false,
    user: null,
    accessToken: null
  });

  private initialized = false;
  private initializationSubject = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private router: Router,
    private appConfig: AppConfigService,
    private modalService: BsModalService
  ) {
    const config = this.appConfig.getAppConfig();
    this.backendUrl = config && config.AppUrl ? config.AppUrl + '/api' : '';

    this.initializeService();
  }

  // Error Handling Methods - Show popup and navigate to error page
  private async showErrorPopupAndNavigate(title: string, message: string, errorCode: string): Promise<void> {
    return new Promise((resolve) => {
      const initialState = {
        alertMode: Mode.Error,
        alertHeader: title,
        alertMassage: message,
        navigateOnExit: true,
        exitNavigationURL: 'error',
        enableNavigation: true,
        backLableText: 'Home',
        navigationURL: 'home'
      };

      this.modalService.show(PopupNotificationComponent, {
        initialState,
        ignoreBackdropClick: true,
        keyboard: false,
      });

      // Navigate to error page after a short delay
      setTimeout(async () => {
        await this.navigateToErrorPage(errorCode, message);
        resolve();
      }, 100);
    });
  }

  private async navigateToErrorPage(errorCode: string, message: string): Promise<void> {
    try {
      await this.router.navigate(['/error'], {
        queryParams: {
          errorCode: errorCode,
          errorMessage: message,
          source: 'ekey-auth-service',
          timestamp: new Date().getTime()
        }
      });
    } catch (navigationError) {
      console.error('Failed to navigate to error page:', navigationError);
    }
  }

  private extractErrorMessage(error: any): string {
    if (!error) return 'An unknown error occurred';

    if (error instanceof HttpErrorResponse) {
      return this.extractHttpErrorMessage(error);
    }

    if (error.error && typeof error.error === 'string') {
      return error.error;
    }

    if (error.error && error.error.message) {
      return error.error.message;
    }

    if (error.message) {
      return error.message;
    }

    if (error.statusText) {
      return error.statusText;
    }

    if (typeof error === 'string') {
      return error;
    }

    return 'An unexpected error occurred';
  }

  private extractHttpErrorMessage(error: HttpErrorResponse): string {
    if (error.error) {
      if (typeof error.error === 'object') {
        const errorObj = error.error;
        return errorObj.message ||
          errorObj.errorDescription ||
          errorObj.title ||
          'Server error: ' + error.status + ' ' + error.statusText;
      }
      if (typeof error.error === 'string') {
        return error.error;
      }
    }

    switch (error.status) {
      case 0:
        return 'Unable to connect to server. Please check your internet connection.';
      case 400:
        return 'Bad request. Please try again.';
      case 401:
        return 'Authentication failed. Please login again.';
      case 403:
        return 'Access denied. You do not have permission.';
      case 404:
        return 'Requested resource not found.';
      case 500:
        return 'Internal server error. Please try again later.';
      case 502:
        return 'Server is temporarily unavailable. Please try again later.';
      case 503:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return 'Server error: ' + error.status + ' ' + error.statusText;
    }
  }

 private handleHttpRequest<T>(request: Observable<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.subscribe({
      next: (result) => resolve(result),
      error: (error: HttpErrorResponse) => {
        const errorMessage = this.extractErrorMessage(error);
        // Show popup and navigate for HTTP errors
        this.showErrorPopupAndNavigate('Request Failed', errorMessage, 'HTTP_ERROR')
          .catch(navError => console.error('Navigation failed:', navError));
        reject(new Error(errorMessage));
      }
    });
  });
}

  // Service Initialization
  private async initializeService(): Promise<void> {
    try {
      console.log('Starting EKeyAuthService initialization...');
      await this.loadConfiguration();
      this.initialized = true;
      this.initializationSubject.next(true);
      console.log('EKeyAuthService initialized successfully');
    } catch (error) {
      console.error('Failed to initialize EKeyAuthService:', error);
      await this.showErrorPopupAndNavigate(
        'Service Initialization Failed',
        'Unable to initialize authentication service. Please refresh the page and try again.',
        'INITIALIZATION_ERROR'
      );
      this.initializationSubject.error(error);
      throw error;
    }
  }

  async waitForInitialization(): Promise<void> {
    if (this.initialized) {
      return;
    }

    return new Promise((resolve, reject) => {
      const subscription = this.initializationSubject.subscribe({
        next: (initialized) => {
          if (initialized) {
            subscription.unsubscribe();
            resolve();
          }
        },
        error: (error) => {
          subscription.unsubscribe();
          reject(error);
        }
      });
    });
  }

  // Configuration Methods
  private async loadConfiguration(): Promise<void> {
    try {
      console.log('Loading configuration...');

      const clientId = await this.getClientId();
      const ekeyBaseUrl = await this.getEkeyBaseUrl();
      const redirectUri = await this.getRedirectUrl();

      this.clientId = clientId;
      this.ekeyBaseUrl = ekeyBaseUrl;
      this.redirectUri = redirectUri;

      const missingConfigs = [];
      if (!this.clientId) missingConfigs.push('Client ID');
      if (!this.ekeyBaseUrl) missingConfigs.push('eKey Base URL');
      if (!this.redirectUri) missingConfigs.push('Redirect URL');

      if (missingConfigs.length > 0) {
        throw new Error('Missing required configuration: ' + missingConfigs.join(', '));
      }

      console.log('Configuration loaded successfully');
    } catch (error) {
      console.error('Configuration loading failed:', error);
      await this.showErrorPopupAndNavigate(
        'Configuration Error',
        'Failed to load application configuration. Please contact support.',
        'CONFIGURATION_ERROR'
      );
      throw error;
    }
  }

  private async getClientId(): Promise<string> {
    try {
      const url = this.backendUrl + '/EKeyAuth/GetClientId?clientId=ClientId';
      const result = await this.handleHttpRequest(this.http.get<any>(url));

      const clientId = (result && result.data) || (result && result.value) || result || '';

      if (!clientId) {
        throw new Error('Empty client ID received from server');
      }

      return clientId;
    } catch (error) {
      console.error('Failed to get client ID:', error);
      await this.showErrorPopupAndNavigate(
        'Configuration Error',
        'Unable to retrieve client ID from server.',
        'CLIENT_ID_ERROR'
      );
      throw error;
    }
  }

  private async getEkeyBaseUrl(): Promise<string> {
    try {
      const url = this.backendUrl + '/EKeyAuth/GetEkeyBaseUrl?ekeyBaseUrl=EkeyBaseUrl';
      const result = await this.handleHttpRequest(this.http.get<any>(url));

      const baseUrl = (result && result.data) || (result && result.value) || result || '';

      if (!baseUrl) {
        throw new Error('Empty eKey base URL received from server');
      }

      return baseUrl;
    } catch (error) {
      console.error('Failed to get eKey base URL:', error);
      await this.showErrorPopupAndNavigate(
        'Configuration Error',
        'Unable to retrieve eKey base URL from server.',
        'BASE_URL_ERROR'
      );
      throw error;
    }
  }

  private async getRedirectUrl(): Promise<string> {
    try {
      const url = this.backendUrl + '/EKeyAuth/GetRedirectUrl?redirectUrl=RedirectUrl';
      const result = await this.handleHttpRequest(this.http.get<any>(url));

      const redirectUrl = (result && result.data) || (result && result.value) || result || '';

      if (!redirectUrl) {
        throw new Error('Empty redirect URL received from server');
      }

      return redirectUrl;
    } catch (error) {
      console.error('Failed to get redirect URL:', error);
      await this.showErrorPopupAndNavigate(
        'Configuration Error',
        'Unable to retrieve redirect URL from server.',
        'REDIRECT_URL_ERROR'
      );
      throw error;
    }
  }

  // Authentication Methods
  async loginWithEKey(): Promise<void> {
    try {
      console.log('Starting eKey authentication process...');

      await this.waitForInitialization();

      const codeVerifier = this.generateCodeVerifier();
      console.log('Code verifier generated');

      const codeChallenge = this.generateCodeChallenge(codeVerifier);
      console.log('Code challenge generated');

      sessionStorage.setItem('ekey_code_verifier', codeVerifier);
      console.log('Code verifier stored in session storage');

      const authUrl = this.buildAuthorizationUrl(codeChallenge);
      console.log('Authorization URL built');

      if (!authUrl || !authUrl.startsWith('http')) {
        throw new Error('Invalid authorization URL generated');
      }

      console.log('Redirecting to eKey authentication...');
      window.location.href = authUrl;

    } catch (error) {
      console.error('Error during eKey login:', error);
      await this.showErrorPopupAndNavigate(
        'Login Failed',
        'Unable to start authentication process. Please try again.',
        'LOGIN_START_ERROR'
      );
      this.updateAuthState(false, null, null);
      throw error;
    }
  }

  private buildAuthorizationUrl(codeChallenge: string): string {
    try {
      if (!this.ekeyBaseUrl) throw new Error('eKey base URL is not configured');
      if (!this.clientId) throw new Error('Client ID is not configured');
      if (!this.redirectUri) throw new Error('Redirect URI is not configured');
      if (!codeChallenge) throw new Error('Code challenge is required');

      const scopeArray = ['openid', 'profile', 'id-bhr', 'phone', 'id-bhr-additional','last_login_time'];
      const scopes = scopeArray.join(' ');

      const params = new URLSearchParams({
        response_type: 'code',
        client_id: this.clientId,
        redirect_uri: this.redirectUri,
        scope: scopes,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        response_mode: 'query',
        prompt: 'consent',
        lang: 'ar-bh'
      });

      const authUrl = this.ekeyBaseUrl + '/oidc/auth?' + params.toString();
      console.log('Final Authorization URL:', authUrl);

      return authUrl;
    } catch (error) {
      console.error('Failed to build authorization URL:', error);
      throw error;
    }
  }

  async handleCallback(code: string): Promise<any> {
    try {
      console.log('Handling eKey authentication callback...');

      await this.waitForInitialization();

      sessionStorage.setItem('ekey_code', code);
      const codeVerifier = sessionStorage.getItem('ekey_code_verifier');

      if (!codeVerifier) {
        throw new Error('Authentication session expired. Please try logging in again.');
      }

      const request: AuthCallbackRequest = { code: code, verifier: codeVerifier };

      console.log('Sending callback request to backend...');
      const response = await this.handleHttpRequest(
        this.http.post(`${this.backendUrl}/EKeyAuth/Callback`, request)
      );

      this.handleAuthenticationSuccess(response);

      console.log('Authentication callback completed successfully');
      return response;
    } catch (error) {
      console.error('Authentication callback failed:', error);
      await this.showErrorPopupAndNavigate(
        'Authentication Failed',
        'Unable to complete authentication. Please try again.',
        'AUTHENTICATION_CALLBACK_ERROR'
      );
      throw error;
    }
  }

async CheckCprNumber(cpr: string): Promise<boolean> {
  console.log("Enter CheckCprNumber Method...............");
  const codeVerifier = sessionStorage.getItem('ekey_code_verifier');
  const code = sessionStorage.getItem('ekey_code');

  console.log("codeVerifier = " + codeVerifier);
  console.log("code = " + code);
  console.log("cpr = " + cpr);

  if (!codeVerifier || !code) {
    throw new Error('Authentication session expired. Please try logging in again.');
  }

  try {
    // Change to GET request with query parameters
    const params = new HttpParams()
      .set('code', code)
      .set('verifier', codeVerifier)
      .set('CPR', cpr);

    const response = await this.http.get<any>(
      `${this.backendUrl}/EKeyAuth/CheckCprNumber`,
      { params }
    ).toPromise();

    // Check different possible response structures
    if (response && typeof response === 'object') {
      if (response.Result === true) return true;
      if (response.result === true) return true;
      if (response.Data && response.Data.Result === true) return true;
      if (response.data && response.data.Result === true) return true;
    }

    return false;
  } catch (error) {
    // Extract the actual error from the function
    const actualError = typeof error === 'function' ? error() : error;
    console.error('CheckCprNumber failed:', actualError);
    
    await this.showErrorPopupAndNavigate(
      'CPR Check Failed',
      'Unable to verify CPR number. Please try again.',
      'CPR_CHECK_ERROR'
    );
    return false;
  }
}

  private handleAuthenticationSuccess(response: any): void {
    try {
      console.log('=== PROCESSING AUTHENTICATION RESPONSE ===');
      console.log('Raw response:', response);

      const responseAny = response as any;
      let actualData = responseAny;

      if (responseAny && responseAny.Data !== undefined) {
        console.log('Found wrapped response structure, using Data property');
        actualData = responseAny.Data;

        if (typeof actualData === 'string') {
          try {
            actualData = JSON.parse(actualData);
          } catch (parseError) {
            console.error('Failed to parse Data as JSON:', parseError);
          }
        }
      }

      console.log('Actual data to process:', actualData);

      let userInfo: any = null;
      let accessToken: string = '';
      let refreshToken: string = '';

      if (actualData && actualData.UserInfo) {
        console.log('Found UserInfo in data');
        userInfo = actualData.UserInfo;
      } else if (actualData && actualData.userInfo) {
        console.log('Found userInfo in data (camelCase)');
        userInfo = actualData.userInfo;
      }

      if (actualData && actualData.AccessToken) {
        console.log('Found AccessToken in data');
        accessToken = actualData.AccessToken;
      } else if (actualData && actualData.accessToken) {
        console.log('Found accessToken in data (camelCase)');
        accessToken = actualData.accessToken;
      }

      if (actualData && actualData.RefreshToken) {
        console.log('Found RefreshToken in data');
        refreshToken = actualData.RefreshToken;
      } else if (actualData && actualData.refreshToken) {
        console.log('Found refreshToken in data (camelCase)');
        refreshToken = actualData.refreshToken;
      }

      console.log('Extracted values:');
      console.log('  userInfo:', userInfo);
      console.log('  accessToken:', accessToken ? '***' + accessToken.substring(accessToken.length - 8) : 'null');
      console.log('  refreshToken:', refreshToken ? '***' + refreshToken.substring(refreshToken.length - 8) : 'null');

      if (!accessToken) {
        const availableKeys = actualData ? Object.keys(actualData).join(', ') : 'data is null';
        throw new Error('No access token received. Available keys in data: ' + availableKeys);
      }

      if (!userInfo) {
        console.warn('No user info found in response');
      }

      this.storeTokens(accessToken, refreshToken);
      this.updateAuthState(true, userInfo, accessToken);

      console.log('Authentication successful');
    } catch (error) {
      console.error('Authentication success handling failed:', error);
      throw error;
    }
  }

  // Token Management
  private storeTokens(accessToken: string, refreshToken: string): void {
    try {
      localStorage.setItem('ekey_access_token', accessToken);
      if (refreshToken) {
        localStorage.setItem('ekey_refresh_token', refreshToken);
      }
    } catch (error) {
      console.error('Failed to store tokens:', error);
      throw error;
    }
  }

  private clearAuthData(): void {
    try {
      localStorage.removeItem('ekey_access_token');
      localStorage.removeItem('ekey_refresh_token');
      sessionStorage.removeItem('ekey_code_verifier');
      sessionStorage.removeItem('ekey_code');
    } catch (error) {
      console.error('Failed to clear auth data:', error);
      throw error;
    }
  }

  private updateAuthState(isAuthenticated: boolean, user: UserInfo | null, accessToken: string | null): void {
    this.authState.next({ isAuthenticated: isAuthenticated, user: user, accessToken: accessToken });
  }

  // Public Methods
  isAuthenticated(): boolean {
    try {
      const token = this.getAccessToken();
      return !!token && !this.isTokenExpired(token);
    } catch (error) {
      console.error('Authentication check failed:', error);
      return false;
    }
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expirationTime = payload.exp * 1000;
      const currentTime = Date.now();
      const bufferTime = 60000;
      return expirationTime - bufferTime < currentTime;
    } catch {
      return true;
    }
  }

  getAuthState(): Observable<AuthState> {
    return this.authState.asObservable();
  }

  getAccessToken(): string | null {
    try {
      return localStorage.getItem('ekey_access_token');
    } catch (error) {
      console.error('Failed to get access token:', error);
      return null;
    }
  }

  getConfig(): any {
    return {
      clientId: this.clientId,
      redirectUri: this.redirectUri,
      ekeyBaseUrl: this.ekeyBaseUrl,
      backendUrl: this.backendUrl,
      initialized: this.initialized
    };
  }

  // PKCE Methods
  private base64UrlEncode(source: any): string {
    try {
      if (source instanceof Uint8Array) {
        const bytes = Array.from(source);
        const binary = bytes.map(byte => String.fromCharCode(byte)).join('');
        const base64 = btoa(binary);
        return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
      } else {
        let base64 = source;
        base64 = base64.replace(/=+$/, '');
        return base64.replace(/\+/g, '-').replace(/\//g, '_');
      }
    } catch (error) {
      console.error('Base64 URL encoding failed:', error);
      throw error;
    }
  }

  generateCodeChallenge(verifier: string): string {
    try {
      const verifierWordArray = CryptoJS.enc.Utf8.parse(verifier);
      const hash = CryptoJS.SHA256(verifierWordArray);
      const hashBase64 = CryptoJS.enc.Base64.stringify(hash);
      return this.base64UrlEncode(hashBase64);
    } catch (error) {
      console.error('Code challenge generation failed:', error);
      throw error;
    }
  }

  generateCodeVerifier(): string {
    try {
      const array = new Uint8Array(32);
      crypto.getRandomValues(array);
      return this.base64UrlEncode(array);
    } catch (error) {
      console.error('Code verifier generation failed:', error);
      throw error;
    }
  }
/*
  refreshToken(refreshToken: string): Observable<any> {
    try {
      if (!refreshToken) {
        throw new Error('No refresh token provided');
      }
      
      return this.http.post(this.backendUrl + '/EKeyAuth/Refresh', { refreshToken: refreshToken }).pipe(
        catchError((error) => {
          const errorMessage = this.extractErrorMessage(error);
          this.showErrorPopupAndNavigate('Token Refresh Failed', errorMessage, 'TOKEN_REFRESH_ERROR')
            .catch(navError => console.error('Navigation failed:', navError));
          return throwError(() => new Error(errorMessage));
        })
      );
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw error;
    }
  }
*/
   refreshToken(refreshToken: string): Observable<any> {
    
    return this.http.post(this.backendUrl + '/EKeyAuth/Refresh', { refreshToken: refreshToken }).pipe(
        // catchError(this.handleError('',))
      );
  }

  logout(): void {
    try {
      this.clearAuthData();
      this.updateAuthState(false, null, null);
      this.router.navigate(['/login']);
    } catch (error) {
      console.error('Logout failed:', error);
      this.showErrorPopupAndNavigate('Logout Error', 'An error occurred during logout.', 'LOGOUT_ERROR')
        .catch(navError => console.error('Navigation failed:', navError));
    }
  }
}